import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Lightbulb } from "lucide-react";
import { db, aggregate, calcNoteTotals, type Note } from "@/lib/storage";
import { formatIDR } from "@/lib/format";

type Insight = { id: string; text: string; tone: "good" | "info" | "warn" };

function buildInsights(notes: Note[]): Insight[] {
  if (notes.length < 3) return [];
  const out: Insight[] = [];

  const today = aggregate(notes, "today");
  const now = new Date();

  // 1) Kemarin vs hari ini (kalau sekarang > jam 12)
  const yStart = new Date(now); yStart.setDate(now.getDate() - 1); yStart.setHours(0, 0, 0, 0);
  const yEnd = new Date(yStart); yEnd.setHours(23, 59, 59, 999);
  const yNotes = notes.filter((n) => {
    const d = new Date(n.date).getTime();
    return d >= yStart.getTime() && d <= yEnd.getTime();
  });
  const yOmset = yNotes.reduce((s, n) => s + calcNoteTotals(n).total, 0);
  if (now.getHours() >= 12 && yOmset > 0) {
    const delta = today.omset - yOmset;
    const pct = Math.round((delta / yOmset) * 100);
    if (Math.abs(pct) >= 15) {
      out.push({
        id: "vs-yesterday",
        tone: delta >= 0 ? "good" : "warn",
        text: delta >= 0
          ? `Omset hari ini ${pct}% lebih tinggi dari kemarin (${formatIDR(today.omset)}).`
          : `Omset hari ini ${Math.abs(pct)}% lebih rendah dari kemarin.`,
      });
    }
  }

  // 2) Item terlaris 30 hari terakhir
  const monthAgo = Date.now() - 30 * 24 * 3600 * 1000;
  const recent = notes.filter((n) => new Date(n.date).getTime() >= monthAgo);
  const itemMap = new Map<string, number>();
  for (const n of recent) {
    for (const it of n.items) {
      const key = it.name.trim().toLowerCase();
      if (!key) continue;
      itemMap.set(key, (itemMap.get(key) || 0) + it.qty);
    }
  }
  const topItem = [...itemMap.entries()].sort((a, b) => b[1] - a[1])[0];
  if (topItem && topItem[1] >= 3) {
    out.push({
      id: "top-item",
      tone: "info",
      text: `Item terlaris 30 hari: "${topItem[0]}" (${topItem[1]}x terjual).`,
    });
  }

  // 3) Hari paling ramai (dow)
  const dowCount = [0, 0, 0, 0, 0, 0, 0];
  for (const n of recent) dowCount[new Date(n.date).getDay()] += 1;
  const total = dowCount.reduce((s, v) => s + v, 0);
  if (total >= 7) {
    const max = Math.max(...dowCount);
    const idx = dowCount.indexOf(max);
    if (max / total >= 0.25) {
      const names = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
      out.push({
        id: "peak-day",
        tone: "info",
        text: `Hari paling ramai bulan ini: ${names[idx]}.`,
      });
    }
  }

  // 4) Pelanggan setia
  const custCount = new Map<string, number>();
  for (const n of recent) {
    const c = n.customerName.trim();
    if (c) custCount.set(c, (custCount.get(c) || 0) + 1);
  }
  const topCust = [...custCount.entries()].sort((a, b) => b[1] - a[1])[0];
  if (topCust && topCust[1] >= 3) {
    out.push({
      id: "top-customer",
      tone: "good",
      text: `${topCust[0]} pelanggan setia — ${topCust[1]}x transaksi bulan ini.`,
    });
  }

  return out.slice(0, 3);
}

export function Insights() {
  const { data: notes = [] } = useQuery({ queryKey: ["notes"], queryFn: () => db.getNotes() });
  const insights = useMemo(() => buildInsights(notes), [notes]);
  if (insights.length === 0) return null;

  return (
    <section className="rounded-2xl border border-border bg-card shadow-soft p-3 space-y-2">
      <div className="flex items-center gap-1.5 t-eyebrow">
        <Lightbulb className="h-3.5 w-3.5" /> Insight otomatis
      </div>
      <ul className="space-y-1.5">
        {insights.map((it) => (
          <li key={it.id} className="text-sm leading-snug flex gap-2">
            <span className={
              "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full " +
              (it.tone === "good" ? "bg-emerald-500" : it.tone === "warn" ? "bg-amber-500" : "bg-primary")
            } />
            <span>{it.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
